import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Loader2, ShieldAlert, XCircle } from 'lucide-react'
import { useState } from 'react'

import { cn } from '@/lib/utils'
import type { ToolCall, ToolResult } from '@/lib/api/support-chat-types'

import { ToolResultSummary } from './tool-result-summary'
import { StructuredDataView } from './structured-data-view'

interface ToolCallCardProps {
  toolCall: ToolCall
  onConfirm?: (pendingActionId: number) => Promise<void>
  onCancel?: (pendingActionId: number) => Promise<void>
}

const FRIENDLY_LABELS: Record<string, string> = {
  lookup_user: 'Looking up attendee',
  get_user_orders: 'Checking orders',
  get_user_transactions: 'Checking token activity',
  get_order: 'Reading order',
  sms_recent_messages: 'Checking SMS history',
  sms_carrier_lookup: 'Checking phone carrier',
  finix_recent_transactions: 'Checking payment attempts',
  resend_verification_sms: 'Resending SMS code',
  regenerate_wallet_pass: 'Issuing new wallet pass',
  clear_user_sessions: 'Clearing sessions',
  mark_order_redeemed: 'Marking order delivered',
  refund_order: 'Refunding order',
  transfer_tokens: 'Transferring tokens',
  redeem_voucher_for_attendee: 'Redeeming voucher',
  update_user_contact: 'Updating contact info',
  suspend_wallet: 'Suspending wallet',
  unsuspend_wallet: 'Unsuspending wallet',
  lookup_vendor: 'Looking up vendor',
  lookup_menu_item: 'Looking up menu item',
  lookup_voucher_redemption: 'Identifying voucher',
  get_event_settings: 'Reading event settings',
  get_vendor_revenue: 'Checking vendor revenue',
  get_top_vendors: 'Ranking vendors',
  get_top_items: 'Ranking items',
  get_top_attendees: 'Ranking attendees',
  list_vendor_admins: 'Listing vendor admins',
  explain_user_permissions: 'Explaining permissions',
  update_menu_item_price: 'Updating item price',
  create_menu_item: 'Adding menu item',
  create_vendor: 'Adding vendor',
  resend_vendor_admin_activation: 'Resending vendor activation',
}

const PENDING_LABELS: Record<string, string> = {
  lookup_user: 'Looking up attendee…',
  get_user_orders: 'Checking orders…',
  get_user_transactions: 'Checking token activity…',
  get_order: 'Reading order…',
  sms_recent_messages: 'Checking SMS history…',
  sms_carrier_lookup: 'Checking phone carrier…',
  finix_recent_transactions: 'Checking payment attempts…',
  resend_verification_sms: 'Sending SMS code…',
  regenerate_wallet_pass: 'Issuing new wallet pass…',
  clear_user_sessions: 'Clearing sessions…',
  mark_order_redeemed: 'Marking order delivered…',
  refund_order: 'Processing refund…',
  transfer_tokens: 'Transferring tokens…',
  redeem_voucher_for_attendee: 'Redeeming voucher…',
  update_user_contact: 'Updating contact info…',
  lookup_vendor: 'Looking up vendor…',
  lookup_menu_item: 'Looking up menu item…',
  lookup_voucher_redemption: 'Identifying voucher…',
  get_event_settings: 'Reading event settings…',
  get_vendor_revenue: 'Checking vendor revenue…',
  get_top_vendors: 'Ranking vendors…',
  get_top_items: 'Ranking items…',
  get_top_attendees: 'Ranking attendees…',
  list_vendor_admins: 'Listing vendor admins…',
  explain_user_permissions: 'Explaining permissions…',
  update_menu_item_price: 'Updating item price…',
  create_menu_item: 'Adding menu item…',
  create_vendor: 'Adding vendor…',
  resend_vendor_admin_activation: 'Resending vendor activation…',
}

export function ToolCallCard({ toolCall, onConfirm, onCancel }: ToolCallCardProps) {
  const { name, args, result } = toolCall
  const [showRaw, setShowRaw] = useState(false)

  const pendingAction = extractPendingAction(result)
  const isPendingProposal =
    !!pendingAction && pendingAction.status === 'pending' && !pendingAction.resolved

  let status: 'pending' | 'ok' | 'error' | 'awaiting_confirm' = !result
    ? 'pending'
    : result.ok
    ? 'ok'
    : 'error'
  if (isPendingProposal) status = 'awaiting_confirm'

  const label =
    status === 'pending'
      ? PENDING_LABELS[name] ?? FRIENDLY_LABELS[name] ?? name
      : FRIENDLY_LABELS[name] ?? name

  const hasRawToShow = result && !isPendingProposal

  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2 text-xs',
        status === 'pending' && 'border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-900',
        status === 'ok' && 'border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40',
        status === 'error' && 'border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40',
        status === 'awaiting_confirm' && 'border-blue-300 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30',
      )}
    >
      <div className="flex items-center gap-2 font-medium text-neutral-700 dark:text-neutral-200">
        {status === 'pending' && <Loader2 className="h-3.5 w-3.5 animate-spin text-neutral-500" />}
        {status === 'ok' && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />}
        {status === 'error' && <AlertTriangle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />}
        {status === 'awaiting_confirm' && <ShieldAlert className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />}
        <span className="text-[12px]">{label}</span>
      </div>

      {isPendingProposal && pendingAction ? (
        <ConfirmBody pendingAction={pendingAction} onConfirm={onConfirm} onCancel={onCancel} />
      ) : (
        result && (
          <>
            <div className="mt-1.5">
              <ToolResultSummary toolCall={toolCall} />
            </div>
            {hasRawToShow && (
              <div className="mt-2 border-t border-neutral-200/60 pt-1.5 dark:border-neutral-800/60">
                <button
                  type="button"
                  onClick={() => setShowRaw((v) => !v)}
                  className="inline-flex items-center gap-1 text-[10px] font-medium text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
                >
                  {showRaw ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                  {showRaw ? 'Hide' : 'View'} details
                </button>
                {showRaw && (
                  <div className="mt-2 space-y-2 rounded bg-white/60 px-2 py-1.5 dark:bg-black/30">
                    {Object.keys(args).length > 0 && (
                      // Args go through the same structured renderer so they
                      // get humanized labels + tag stripping for consistency.
                      // The LLM-supplied query/ids are usually trivial here.
                      <div className="border-b border-neutral-200/60 pb-1.5 dark:border-neutral-800/60">
                        <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-neutral-400 dark:text-neutral-500">
                          Information
                        </div>
                        <StructuredDataView data={args} />
                      </div>
                    )}
                    {result.ok ? (
                      <StructuredDataView data={result.data} />
                    ) : (
                      <div className="text-[11px] text-amber-700 dark:text-amber-400">
                        <span className="font-mono">{result.error}</span>: {result.message}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )
      )}
    </div>
  )
}

interface PendingProposal {
  id: number
  summary: string
  expires_at: string
  status: 'pending' | 'confirmed' | 'cancelled' | 'expired'
  resolved: boolean
}

function extractPendingAction(result: ToolResult | undefined): PendingProposal | null {
  if (!result || !result.ok) return null
  const data = result.data as Record<string, unknown>
  const id = data?.pending_action_id
  if (typeof id !== 'number') return null
  return {
    id,
    summary: typeof data.summary === 'string' ? data.summary : 'Confirm this action',
    expires_at: typeof data.expires_at === 'string' ? data.expires_at : '',
    status: (data.status as PendingProposal['status']) || 'pending',
    resolved: typeof data.resolved_at === 'string',
  }
}

function ConfirmBody({
  pendingAction,
  onConfirm,
  onCancel,
}: {
  pendingAction: PendingProposal
  onConfirm?: (pendingActionId: number) => Promise<void>
  onCancel?: (pendingActionId: number) => Promise<void>
}) {
  const [submitting, setSubmitting] = useState<'confirm' | 'cancel' | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handle = async (kind: 'confirm' | 'cancel') => {
    const fn = kind === 'confirm' ? onConfirm : onCancel
    if (!fn || submitting) return
    setError(null)
    setSubmitting(kind)
    try {
      await fn(pendingAction.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : `${kind} failed`)
    } finally {
      setSubmitting(null)
    }
  }

  return (
    <div className="mt-2 space-y-2">
      <p className="text-sm text-blue-900 dark:text-blue-100">{pendingAction.summary}</p>
      <p className="text-[10px] text-neutral-500 dark:text-neutral-400">
        Expires {pendingAction.expires_at ? new Date(pendingAction.expires_at).toLocaleTimeString() : 'soon'}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => handle('confirm')}
          disabled={!!submitting || !onConfirm}
          className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting === 'confirm' ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
          Confirm
        </button>
        <button
          type="button"
          onClick={() => handle('cancel')}
          disabled={!!submitting || !onCancel}
          className="inline-flex items-center gap-1 rounded-md border border-neutral-300 bg-white px-2.5 py-1 text-xs font-medium text-neutral-700 hover:bg-neutral-50 disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200"
        >
          {submitting === 'cancel' ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
          Cancel
        </button>
      </div>
      {error && <p className="text-[11px] text-red-600 dark:text-red-400">{error}</p>}
    </div>
  )
}

